// src/components/settings/team-defaults-tab.jsx
// Your team's default vendor contacts, by category (Title / Stager / Inspector /
// Lender). Each category can hold MULTIPLE people; the FIRST is the default.
// The first Title contact is synced to the flat titleCompany/titleCloser* keys
// so the acceptance emails + turn-in pre-fill keep working unchanged.
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  saveTeamDefaults,
  TEAM_DEFAULTS_FALLBACK,
} from "../../utils/teamDefaults";

const CATEGORIES = [
  { key: "title", label: "Title", hasCompany: true },
  { key: "stager", label: "Stager", hasCompany: false },
  { key: "inspector", label: "Inspector", hasCompany: false },
  { key: "lender", label: "Lender", hasCompany: false },
];

const blankContact = (hasCompany) =>
  hasCompany
    ? { company: "", name: "", email: "", phone: "" }
    : { name: "", email: "", phone: "" };

// Build each category's contact list from the saved doc — prefer the new
// per-category arrays, else migrate the old single default fields.
function buildLists(td) {
  const d = td || {};
  const title =
    Array.isArray(d.titleContacts) && d.titleContacts.length
      ? d.titleContacts
      : [
          {
            company: d.titleCompany || "",
            name: d.titleCloserName || "",
            email: d.titleCloserEmail || "",
            phone: d.titleCloserPhone || "",
          },
        ];
  const one = (arrKey, name, email, phone) =>
    Array.isArray(d[arrKey]) && d[arrKey].length
      ? d[arrKey]
      : [{ name: d[name] || "", email: d[email] || "", phone: d[phone] || "" }];
  return {
    title,
    stager: one("stagerContacts", "stagerName", "stagerEmail", "stagerPhone"),
    inspector: one(
      "inspectorContacts",
      "inspectorName",
      "inspectorEmail",
      "inspectorPhone",
    ),
    lender: one("lenderContacts", "lenderName", "lenderEmail", "lenderPhone"),
  };
}

const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1E293B]";

export default function TeamDefaultsTab() {
  const [{ user, teamDefaults }, dispatch] = useStateValue();
  const [lists, setLists] = useState(() => buildLists(teamDefaults));
  const [fee, setFee] = useState(
    (teamDefaults || TEAM_DEFAULTS_FALLBACK).brokerAdminFee ?? "",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (teamDefaults) {
      setLists(buildLists(teamDefaults));
      setFee(teamDefaults.brokerAdminFee ?? "");
    }
  }, [teamDefaults]);

  const editContact = (cat, i, key, value) =>
    setLists((l) => ({
      ...l,
      [cat]: l[cat].map((c, idx) => (idx === i ? { ...c, [key]: value } : c)),
    }));

  const addContact = (cat, hasCompany) =>
    setLists((l) => ({ ...l, [cat]: [...l[cat], blankContact(hasCompany)] }));

  const removeContact = (cat, i) =>
    setLists((l) => ({ ...l, [cat]: l[cat].filter((_, idx) => idx !== i) }));

  const onSave = async () => {
    setSaving(true);
    try {
      const first = lists.title[0] || {};
      const payload = {
        ...(teamDefaults || TEAM_DEFAULTS_FALLBACK),
        titleContacts: lists.title,
        stagerContacts: lists.stager,
        inspectorContacts: lists.inspector,
        lenderContacts: lists.lender,
        // Keep the flat title keys synced to the first/default title contact.
        titleCompany: first.company || "",
        titleCloserName: first.name || "",
        titleCloserEmail: first.email || "",
        titleCloserPhone: first.phone || "",
        brokerAdminFee: Number(fee) || 0,
      };
      await saveTeamDefaults(payload, user?.id);
      dispatch({ type: actionType.SET_TEAM_DEFAULTS, payload });
      toast.success("Team Defaults saved");
    } catch (err) {
      console.error("saveTeamDefaults failed:", err);
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Team Defaults</h3>
        <p className="text-sm text-slate-500 mt-1">
          Your go-to vendor contacts by category, and the broker admin fee. Each
          category can hold several people — the first is your default. Title
          pre-fills your acceptance emails and turn-ins.
        </p>
      </div>

      {CATEGORIES.map((cat) => (
        <div key={cat.key}>
          <h4 className="text-sm font-semibold text-slate-800 mb-2">
            {cat.label}
          </h4>
          <div className="space-y-3">
            {lists[cat.key].map((c, i) => (
              <div
                key={i}
                className="border border-slate-200 rounded-xl p-3 bg-white"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-500">
                    {i === 0 ? "Default" : `${cat.label} ${i + 1}`}
                  </span>
                  {lists[cat.key].length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContact(cat.key, i)}
                      className="text-red-500 hover:text-red-600"
                      title="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {cat.hasCompany && (
                    <input
                      className={`${inputCls} sm:col-span-2`}
                      placeholder="Company"
                      value={c.company ?? ""}
                      onChange={(e) =>
                        editContact(cat.key, i, "company", e.target.value)
                      }
                    />
                  )}
                  <input
                    className={`${inputCls} ${cat.hasCompany ? "sm:col-span-2" : "sm:col-span-2"}`}
                    placeholder={cat.hasCompany ? "Closer name" : "Name / company"}
                    value={c.name ?? ""}
                    onChange={(e) =>
                      editContact(cat.key, i, "name", e.target.value)
                    }
                  />
                  <input
                    className={inputCls}
                    placeholder="Email"
                    value={c.email ?? ""}
                    onChange={(e) =>
                      editContact(cat.key, i, "email", e.target.value)
                    }
                  />
                  <input
                    className={inputCls}
                    placeholder="Phone"
                    value={c.phone ?? ""}
                    onChange={(e) =>
                      editContact(cat.key, i, "phone", e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addContact(cat.key, cat.hasCompany)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              <Plus size={15} /> Add another {cat.label.toLowerCase()}
            </button>
          </div>
        </div>
      ))}

      <div>
        <h4 className="text-sm font-semibold text-slate-800 mb-1">
          Broker Admin Fee
        </h4>
        <p className="text-xs text-slate-500 mb-2">
          Default admin fee ($) on each deal's commission. Overridable per deal.
        </p>
        <input
          type="number"
          className={`${inputCls} max-w-[200px]`}
          placeholder="599"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
        />
      </div>

      <div className="pt-1">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-themeColor hover:bg-themeColor/90 text-white disabled:opacity-50"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
